import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Modal, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import { ListarEstadosResultados, ListarTipoDocResultados } from "../../Service/Service.util";
import { ListaruserSinPagineo } from "../../Service/Service.users";
import { PostResultadosExamenes, PutResultadosExamenes } from "../../Service/Service.resultadosexamenes";
import {
    getResultadoDocumentos,
    getResultadoDocumentoBlob,
    descargarResultadoDocumento,
    createResultadoDocumentoLink,
    uploadResultadoDocumento,
    replaceResultadoDocumento,
    replaceResultadoDocumentoUpload,
    deleteResultadoDocumento,
} from "../../Service/Service.resultadoDocumento";
import SelectSearchable from "../SelectSeachble/SelectSearchble";
import ModalPreviewResultadoDocumento from "./ModalPreviewResultadoDocumento";
import { estiloEstado } from "./estadoColor";
import usePermissions from "../../hook/usePermissions";

// RESULTADOS-06E — sustituye a ModalUploadResultado en el flujo /resultados.
//
// Reglas duras de esta UX:
//   - NUNCA construye `multimedia:[...]` para POST/PUT ni usa SubirMedia().
//   - NUNCA abre `documento.archivo_url` ni navega a /store/. "Ver"/"Descargar"
//     pasan SIEMPRE por los endpoints autenticados 06D.
//   - La fuente de documentos es EXCLUSIVAMENTE `resultado_examen_documento`
//     (endpoint 06B). Nunca hace fallback al bag `multimedia` legacy.
//
// Fase 1: contexto, estado/usuarios (crear en 2 etapas / editar), lista de
//         documentos (activos + historicos) con Ver/Abrir.
// Fase 2: sub-form Agregar (archivo / link / ambos), Reemplazar (versionado),
//         Quitar (desactivacion logica), Descargar (attachment autenticado).

const EXT_PERMITIDAS = ["pdf", "jpg", "jpeg", "png"];
const MAX_BYTES = 10 * 1024 * 1024;

// RESULTADOS-06G: iconos SVG inline (sin dependencias: @ant-design/icons arrastra
// un ESM que el transform de Jest/CRA no procesa). 16px, heredan el color del botón.
const Svg = ({ children }) => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
        strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        focusable="false" style={{ verticalAlign: "-2px" }}>{children}</svg>
);
const IcoVer = () => <Svg><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z" /><circle cx="8" cy="8" r="2" /></Svg>;
const IcoDescargar = () => <Svg><path d="M8 1v9" /><path d="M4.5 7 8 10.5 11.5 7" /><path d="M2 14h12" /></Svg>;
const IcoAbrir = () => <Svg><path d="M6 2H2v12h12v-4" /><path d="M9 2h5v5" /><path d="M7 9 14 2" /></Svg>;
const IcoReemplazar = () => <Svg><path d="M2 6a6 6 0 0 1 10-2l2 2" /><path d="M14 2v4h-4" /><path d="M14 10a6 6 0 0 1-10 2l-2-2" /><path d="M2 14v-4h4" /></Svg>;
const IcoQuitar = () => <Svg><path d="M2 4h12" /><path d="M6 4V2h4v2" /><path d="M4 4l1 10h6l1-10" /></Svg>;

function ModalResultadoExamen({ data }) {
    const { show, handleClose, invoice } = data;
    const { hasPermission } = usePermissions();

    // RESULTADOS-06G: la re-inicialización del modal (resetLocal + recarga de
    // catálogos/documentos) se dispara SOLO al abrir o al cambiar de fila real,
    // nunca porque el padre haya recreado el objeto `invoice` (p.ej. tras un
    // refetch de la Grid). Se identifica la fila por su clave estable, y el
    // contenido de `invoice` se lee por ref para no re-crear callbacks.
    const invoiceRef = useRef(invoice);
    invoiceRef.current = invoice;
    const invoiceKey = [
        invoice?.result_key ?? "",
        invoice?.resultado?.id ?? "",
        invoice?.factura_detalle_id ?? "",
        invoice?.factura_id ?? "",
    ].join("|");

    const [estados, setEstados] = useState([]);
    const [estadoSel, setEstadoSel] = useState(null);
    const [users, setUsers] = useState([]);
    const [userResp, setUserResp] = useState(null);
    const [userInf, setUserInf] = useState(null);
    // RESULTADOS-FECHA-REALIZACION: cuándo se REALIZÓ el examen. No es la fecha
    // de la factura ni la de subida del documento -- una placa tomada el lunes
    // puede facturarse antes y subirse dos días después, así que ninguna de las
    // otras sirve de sustituto. Cadena "YYYY-MM-DD" o "" (sin registrar).
    const [fechaRealizacion, setFechaRealizacion] = useState("");
    const [saving, setSaving] = useState(false);

    const [resultadoId, setResultadoId] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [showHistoricos, setShowHistoricos] = useState(false);

    // RESULTADOS-06H: el resultado se crea AUTOMÁTICAMENTE en la primera acción
    // real (agregar documento / pulsar Guardar), nunca al abrir/cerrar el modal.
    // `resultadoIdRef` cierra la ventana de carrera entre el POST y el re-render;
    // `ensureResultadoRef` deduplica dos acciones rápidas -> un solo resultado.
    const resultadoIdRef = useRef(null);
    resultadoIdRef.current = resultadoId;
    const ensureResultadoRef = useRef(null);
    // se marca cuando el usuario elige explícitamente un estado -> desactiva el
    // auto "Pendiente Informe" al cerrar.
    const estadoTocadoRef = useRef(false);
    // RESULTADOS-06H-QA: el auto "Pendiente Informe" al cerrar SOLO aplica si en
    // ESTA sesión del modal se agregó un documento de tipo != "Informe". Abrir
    // un resultado solo para mirarlo y cerrarlo NUNCA cambia su estado.
    const docNoInformeSesionRef = useRef(false);
    const cerrandoRef = useRef(false);
    const formRef = useRef({});
    formRef.current = { estadoSel, userResp, userInf, documentos, fechaRealizacion };

    // --- sub-form de documentos (Fase 2) ---
    const [tipos, setTipos] = useState([]);
    const [docFormOpen, setDocFormOpen] = useState(false);
    const [docMode, setDocMode] = useState("CREATE"); // "CREATE" | "REPLACE"
    const [replaceTarget, setReplaceTarget] = useState(null);
    const [docTipo, setDocTipo] = useState(null);
    const [docFile, setDocFile] = useState(null);
    const [docFileError, setDocFileError] = useState("");
    const [docLink, setDocLink] = useState("");
    const [docDesc, setDocDesc] = useState("");
    const [docSaving, setDocSaving] = useState(false);
    const [docActionId, setDocActionId] = useState(null); // fila con accion (quitar/descargar) en curso
    const [lastDocId, setLastDocId] = useState(null); // ultimo documento agregado/reemplazado -> resaltado temporal
    const lastDocTimer = useRef(null);
    const filaNuevaRef = useRef(null);

    const marcarUltimoDocumento = useCallback((id) => {
        if (!id) return;
        setLastDocId(id);
        if (lastDocTimer.current) clearTimeout(lastDocTimer.current);
        lastDocTimer.current = setTimeout(() => setLastDocId(null), 4000);
    }, []);
    useEffect(() => () => { if (lastDocTimer.current) clearTimeout(lastDocTimer.current); }, []);
    useEffect(() => {
        if (!lastDocId || !filaNuevaRef.current) return;
        try { filaNuevaRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e) { /* noop */ }
    }, [lastDocId, documentos]);

    // --- RESULTADOS-06F: previsualización interna del archivo ("Ver") ---
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewType, setPreviewType] = useState(null);
    const [previewLoadingId, setPreviewLoadingId] = useState(null);

    const revocarPreview = useCallback((url) => {
        if (url) { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } }
    }, []);

    const cerrarPreview = useCallback(() => {
        setPreviewUrl((prev) => { revocarPreview(prev); return null; });
        setPreviewDoc(null);
        setPreviewType(null);
        setPreviewOpen(false);
    }, [revocarPreview]);

    const yaExiste = !!resultadoId;
    const esLegacy = invoice?.resultado_es_legacy === true;
    const puedeGuardar = hasPermission(invoice?.resultado ? "resultados.edit" : "resultados.create");
    const puedeEditarDocs = hasPermission("resultados.edit");

    const resetDocumentForm = useCallback(() => {
        setDocFormOpen(false);
        setDocMode("CREATE");
        setReplaceTarget(null);
        setDocTipo(null);
        setDocFile(null);
        setDocFileError("");
        setDocLink("");
        setDocDesc("");
        setDocSaving(false);
    }, []);

    const resetLocal = useCallback(() => {
        setEstadoSel(null);
        setUserResp(null);
        setUserInf(null);
        setFechaRealizacion(String(invoiceRef.current?.resultado?.fecha_realizacion ?? "").slice(0, 10));
        setDocumentos([]);
        setShowHistoricos(false);
        setSaving(false);
        setDocActionId(null);
        setLastDocId(null);
        const ridInicial = invoiceRef.current?.resultado?.id ?? null;
        setResultadoId(ridInicial);
        resultadoIdRef.current = ridInicial;
        ensureResultadoRef.current = null;
        estadoTocadoRef.current = false;
        docNoInformeSesionRef.current = false;
        cerrandoRef.current = false;
        resetDocumentForm();
        cerrarPreview();
    }, [resetDocumentForm, cerrarPreview]);

    // revoca el objectURL de preview al desmontar
    useEffect(() => () => { setPreviewUrl((u) => { revocarPreview(u); return null; }); }, [revocarPreview]);

    const cargarDocumentos = useCallback(async (rid, historicos) => {
        if (!rid) { setDocumentos([]); return; }
        setLoadingDocs(true);
        const res = await getResultadoDocumentos(rid, historicos);
        if (res && res.success) setDocumentos(res.data || []);
        setLoadingDocs(false);
    }, []);

    // Cada catálogo va en su propio try: `ListarEstadosResultados` y
    // `ListarTipoDocResultados` NO capturan, así que un 403 en el primero
    // lanzaba y dejaba el modal a medias -- sin usuarios, sin tipos y sin
    // decir nada. Un catálogo que falta degrada el formulario; no debe
    // impedir ver los documentos, que es a lo que se entra la mayoría de veces.
    const cargarCatalogos = useCallback(async () => {
        const inv = invoiceRef.current;
        try {
        const est = await ListarEstadosResultados();
        if (est && est.success) {
            const opts = est.data.map((e) => ({ value: e.id, label: e.nombre }));
            setEstados(opts);
            if (inv?.resultado) {
                setEstadoSel(opts.find((o) => o.value === inv.resultado.estado_id) || null);
            } else {
                setEstadoSel(opts.find((o) => (o.label || "").trim().toLowerCase() === "pendiente") || null);
            }
        }
        } catch (e) { /* sin catálogo de estados: el resto del modal sigue */ }

        try {
        const us = await ListaruserSinPagineo();
        if (us && us.success) {
            const uopts = us.data.map((u) => ({ value: u.id, label: u.full_name, ...u }));
            setUsers(uopts);
            if (inv?.resultado) {
                setUserResp(uopts.find((o) => o.value === inv.resultado.usuario_id) || null);
                setUserInf(uopts.find((o) => o.value === inv.resultado.usuario_informa_id) || null);
            }
        }
        } catch (e) { /* sin catálogo de usuarios */ }

        try {
        const td = await ListarTipoDocResultados();
        if (td && td.success) {
            // el backend ya devuelve solo tipos visibles para la compania y solo activos
            setTipos((td.data || []).map((t) => ({ value: t.id, label: t.tipo, ...t })));
        }
        } catch (e) { /* sin catálogo de tipos de documento */ }
    }, []);

    // Re-inicializa SOLO al abrir o al cambiar de fila (invoiceKey), nunca porque
    // el padre haya recreado el objeto `invoice`. Las acciones documentales
    // (agregar/reemplazar/quitar) actualizan la lista localmente y NO re-disparan
    // esto -> el modal no pierde contexto ni se cierra.
    useEffect(() => {
        if (!show) return;
        resetLocal();
        cargarCatalogos();
        const rid = invoiceRef.current?.resultado?.id ?? null;
        if (rid) cargarDocumentos(rid, false);
    }, [show, invoiceKey, resetLocal, cargarCatalogos, cargarDocumentos]);

    useEffect(() => {
        if (show && resultadoId) cargarDocumentos(resultadoId, showHistoricos);
    }, [showHistoricos]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------- resultado logico ----------
    // RESULTADOS-06H: crea el resultado si aún no existe y devuelve su id.
    // Idempotente y a prueba de doble ejecución concurrente (ref de promesa +
    // ref de id). NUNCA se llama al abrir/cerrar: solo desde "Agregar documento"
    // o "Guardar".
    const ensureResultado = async () => {
        if (resultadoIdRef.current) return resultadoIdRef.current;
        if (ensureResultadoRef.current) return ensureResultadoRef.current;
        const inv = invoiceRef.current;
        const { estadoSel: es, userResp: ur, userInf: ui, fechaRealizacion: fr } = formRef.current;
        const run = (async () => {
            // RESULTADOS-18: una prestación sin factura (SEGURO100 $0, particular
            // aún sin facturar) se identifica por `procedimiento_cita_paciente_id`.
            // NUNCA se envía `factura_id: 0` ni un id ficticio.
            const sinFactura = inv.sin_factura === true || inv.factura_id == null;
            const payload = {
                resultado_scope: inv.result_type,
                ...(sinFactura
                    ? { procedimiento_cita_paciente_id: inv.procedimiento_cita_paciente_id }
                    : {
                        factura_id: inv.factura_id,
                        ...(inv.result_type === "INDIVIDUAL" ? { factura_detalle_id: inv.factura_detalle_id } : {}),
                    }),
                estado_id: es?.value,
                usuario_id: ur?.value ?? null,
                usuario_informa_id: ui?.value ?? null,
                fecha_realizacion: fr || null,
            };
            const res = await PostResultadosExamenes(payload);
            if (res && res.success && res.data?.id) {
                resultadoIdRef.current = res.data.id;
                setResultadoId(res.data.id);
                return res.data.id;
            }
            Swal.fire({ icon: "error", title: "No se pudo crear el resultado", text: res?.message || "Error" });
            return null;
        })();
        ensureResultadoRef.current = run;
        try { return await run; } finally { ensureResultadoRef.current = null; }
    };

    // "Guardar": persiste estado/usuarios explícitamente. Para un resultado
    // nuevo lo crea (ensureResultado); para uno existente versiona vía PUT.
    // En ambos casos refresca la grilla y cierra.
    const handleGuardar = async () => {
        if (saving) return;
        setSaving(true);
        try {
            if (!resultadoIdRef.current) {
                const rid = await ensureResultado();
                if (!rid) return;
                handleClose(true);
                Swal.fire({ icon: "success", title: "Resultado creado", timer: 1800, showConfirmButton: false });
                return;
            }
            const res = await PutResultadosExamenes(resultadoIdRef.current, {
                estado_id: estadoSel?.value,
                usuario_id: userResp?.value ?? null,
                usuario_informa_id: userInf?.value ?? null,
                fecha_realizacion: fechaRealizacion || null,
            });
            if (res && res.success) {
                handleClose(true);
                Swal.fire({ icon: "success", title: "Resultado guardado", text: res.message, timer: 1800, showConfirmButton: false });
            } else {
                Swal.fire({ icon: "error", title: "No se pudo guardar", text: res?.message || "Error" });
            }
        } finally {
            setSaving(false);
        }
    };

    // RESULTADOS-06H: al cerrar, si el resultado sigue "Pendiente" pero ya tiene
    // >=1 documento que NO es "Informe" y el usuario no eligió otro estado,
    // se promueve a "Pendiente Informe" (PUT). Nunca crea resultados; nunca
    // toca Finalizado/Cancelado ni resultados sin documentos. Devuelve true si
    // hubo cambio (para forzar recarga de la grilla).
    const maybeAutoPendienteInforme = async () => {
        if (!resultadoIdRef.current || estadoTocadoRef.current) return false;
        if (!docNoInformeSesionRef.current) return false; // solo si se agregó un doc != Informe en esta sesión
        const { estadoSel: es, userResp: ur, userInf: ui, documentos: docs } = formRef.current;
        if ((es?.label || "").trim().toLowerCase() !== "pendiente") return false;
        const activos = (docs || []).filter((d) => d.estado === "Activo");
        if (activos.length === 0) return false;
        const hayNoInforme = activos.some((d) => (d.tipo_documento_nombre || "").trim().toLowerCase() !== "informe");
        if (!hayNoInforme) return false;
        const destino = estados.find((e) => (e.label || "").trim().toLowerCase() === "pendiente informe");
        if (!destino) return false;
        const res = await PutResultadosExamenes(resultadoIdRef.current, {
            estado_id: destino.value,
            usuario_id: ur?.value ?? null,
            usuario_informa_id: ui?.value ?? null,
        });
        return !!(res && res.success);
    };

    const cerrarModal = async (reload) => {
        if (cerrandoRef.current) return;
        cerrandoRef.current = true;
        let avanzo = false;
        try { avanzo = await maybeAutoPendienteInforme(); } catch (e) { /* no bloquear el cierre */ }
        cerrandoRef.current = false;
        handleClose(reload || avanzo);
    };

    // ---------- documentos: ver (preview interno) / abrir / descargar ----------
    // RESULTADOS-06F: "Ver" abre un Modal de previsualización DENTRO de Smartmedic
    // (iframe para PDF, <img> para imagen). NUNCA nueva pestaña, NUNCA archivo_url.
    const handleVerArchivo = async (doc) => {
        if (previewLoadingId) return; // anti doble click mientras se obtiene el blob
        setPreviewLoadingId(doc.id);
        const r = await getResultadoDocumentoBlob(resultadoId, doc.id); // sin download=1
        setPreviewLoadingId(null);
        if (!r || !r.success) {
            // 404/403/genérico -> mensaje controlado, NO se abre el preview, sin fallback
            Swal.fire({ icon: "warning", title: "Documento", text: mensajeDoc(r) });
            return;
        }
        setPreviewUrl((prev) => { revocarPreview(prev); return URL.createObjectURL(r.blob); });
        setPreviewType(r.contentType);
        setPreviewDoc(doc);
        setPreviewOpen(true);
    };

    const handleDescargar = async (doc) => {
        if (docActionId) return;
        setDocActionId(doc.id);
        const r = await descargarResultadoDocumento(resultadoId, doc.id, { filenameHint: nombreDescarga(doc) });
        setDocActionId(null);
        if (!r || !r.success) Swal.fire({ icon: "warning", title: "Documento", text: mensajeDoc(r) });
    };

    const handleAbrirLink = (doc) => {
        try {
            const u = new URL(doc.link_url);
            if (u.protocol !== "http:" && u.protocol !== "https:") throw new Error("scheme");
            window.open(doc.link_url, "_blank", "noopener,noreferrer");
        } catch (e) {
            Swal.fire({ icon: "warning", title: "Enlace", text: "El enlace del documento no es válido." });
        }
    };

    // ---------- documentos: agregar ----------
    const abrirSubFormCreate = () => {
        resetDocumentForm();
        setDocMode("CREATE");
        setDocFormOpen(true);
    };

    const abrirSubFormReplace = (doc) => {
        Swal.fire({
            title: "Reemplazar documento",
            text: "El documento actual se conservará como versión anterior.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Continuar",
            cancelButtonText: "Cancelar",
        }).then((r) => {
            if (!r.isConfirmed) return;
            resetDocumentForm();
            setDocMode("REPLACE");
            setReplaceTarget(doc);
            setDocTipo(tipos.find((t) => t.value === doc.tipo_documento_id) || null);
            setDocDesc(doc.descripcion || "");
            if (doc.link_url && !doc.tiene_archivo) setDocLink(doc.link_url); // solo si el actual era link-only
            setDocFormOpen(true);
        });
    };

    const handleFileChange = (e) => {
        const f = e.target.files && e.target.files[0];
        setDocFileError("");
        if (!f) { setDocFile(null); return; }
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        if (!EXT_PERMITIDAS.includes(ext)) {
            setDocFile(null);
            setDocFileError("Formato no permitido. Solo PDF, JPG, JPEG o PNG.");
            e.target.value = "";
            return;
        }
        if (f.size > MAX_BYTES) {
            setDocFile(null);
            setDocFileError("El archivo excede el tamaño máximo permitido (10 MB).");
            e.target.value = "";
            return;
        }
        setDocFile(f);
    };

    const linkValido = (raw) => {
        const s = String(raw || "").trim();
        if (!s) return null; // opcional
        try {
            const u = new URL(s);
            if (u.protocol !== "http:" && u.protocol !== "https:") return false;
            return s;
        } catch (e) {
            return false;
        }
    };

    const handleAgregarOReemplazar = async () => {
        if (docSaving) return;
        if (!docTipo) {
            Swal.fire({ icon: "warning", title: "Tipo de documento", text: "Selecciona un tipo de documento." });
            return;
        }
        const linkNorm = linkValido(docLink);
        if (linkNorm === false) {
            Swal.fire({ icon: "warning", title: "Enlace", text: "El enlace no es válido (solo http/https)." });
            return;
        }
        if (!docFile && !linkNorm) {
            Swal.fire({ icon: "warning", title: "Contenido", text: "Adjunta un archivo o ingresa un enlace." });
            return;
        }

        setDocSaving(true);
        const tipoId = docTipo.value;
        const desc = docDesc.trim() || undefined;

        // RESULTADOS-06H: para AGREGAR, si el resultado aún no existe se crea
        // aquí automáticamente (Pendiente) antes de subir el documento. El
        // usuario nunca tuvo que pulsar "Crear resultado".
        let rid = resultadoIdRef.current;
        if (docMode !== "REPLACE" && !rid) {
            rid = await ensureResultado();
            if (!rid) { setDocSaving(false); return; } // POST falló: error ya mostrado, sub-form abierto
        }

        let res;
        if (docMode === "REPLACE") {
            const did = replaceTarget.id;
            if (docFile) {
                res = await replaceResultadoDocumentoUpload(rid, did, {
                    tipo_documento_id: tipoId, descripcion: desc, link_url: linkNorm || undefined, archivo: docFile,
                });
            } else {
                res = await replaceResultadoDocumento(rid, did, {
                    tipo_documento_id: tipoId, link_url: linkNorm, descripcion: desc,
                });
            }
        } else if (docFile) {
            // archivo (con o sin link) -> una sola fila via 06C
            res = await uploadResultadoDocumento(rid, {
                tipo_documento_id: tipoId, descripcion: desc, link_url: linkNorm || undefined, archivo: docFile,
            });
        } else {
            // link-only -> 06B
            res = await createResultadoDocumentoLink(rid, {
                tipo_documento_id: tipoId, link_url: linkNorm, descripcion: desc,
            });
        }
        setDocSaving(false);

        if (res && res.success) {
            // el auto "Pendiente Informe" al cerrar solo se arma si en esta sesión
            // se agregó (no reemplazó) un documento que NO es "Informe".
            if (docMode !== "REPLACE" && (docTipo?.label || "").trim().toLowerCase() !== "informe") {
                docNoInformeSesionRef.current = true;
            }
            resetDocumentForm();
            // el modal principal permanece abierto: solo se refresca la lista local
            await cargarDocumentos(rid, showHistoricos);
            marcarUltimoDocumento(res.data?.id ?? null);
            Swal.fire({
                icon: "success",
                title: docMode === "REPLACE" ? "Documento reemplazado" : "Documento agregado",
                timer: 1600, showConfirmButton: false,
            });
        } else {
            const s = res?.status;
            if (s === 409) {
                await cargarDocumentos(rid, showHistoricos);
                resetDocumentForm();
                Swal.fire({ icon: "warning", title: "Conflicto", text: "El documento ya fue modificado o reemplazado." });
            } else {
                // mantener el sub-form abierto con tipo/link/descripcion; el <input file>
                // del navegador no se puede repoblar por seguridad -> se pide reselección.
                setDocFile(null);
                Swal.fire({ icon: "error", title: "No se pudo guardar el documento", text: res?.message || "Error" });
            }
        }
    };

    // ---------- documentos: quitar ----------
    const handleQuitar = (doc) => {
        if (docActionId) return;
        Swal.fire({
            title: "¿Deseas quitar este documento?",
            text: "El documento dejará de mostrarse entre los activos, pero permanecerá disponible en el historial.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Quitar",
            cancelButtonText: "Cancelar",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            setDocActionId(doc.id);
            const res = await deleteResultadoDocumento(resultadoId, doc.id);
            setDocActionId(null);
            if (res && res.success) {
                setLastDocId((p) => (p === doc.id ? null : p));
                await cargarDocumentos(resultadoId, showHistoricos); // modal sigue abierto
            } else {
                Swal.fire({ icon: "error", title: "No se pudo quitar", text: res?.message || "Error" });
            }
        });
    };

    // ---------- helpers ----------
    const mensajeDoc = (r) =>
        r?.status === 403 ? "No tienes permiso para consultar este archivo." :
        r?.status === 404 ? "El archivo no está disponible en este entorno." :
        "No se pudo obtener el documento.";

    const nombreDescarga = (doc) => {
        const t = (doc.tipo_documento_nombre || "documento").replace(/[^a-zA-Z0-9-_]+/g, "_");
        return `${t}-${doc.id}`;
    };

    const hostDe = (url) => {
        try { return new URL(url).host; } catch (e) { return url; }
    };

    const docsActivos = documentos.filter((d) => d.estado === "Activo");
    const badgeEstado = estadoSel?.label || invoice?.resultado?.estados?.nombre || "Pendiente";

    // RESULTADOS-06G: acciones por fila con icono (no texto). Se conserva
    // `aria-label` + `title` con la palabra para accesibilidad / tooltip.
    const accionesFila = (d) => {
        const esActivo = d.estado === "Activo";
        const enCurso = docActionId === d.id;
        const cargandoVer = previewLoadingId === d.id;
        return (
            <>
                {d.tiene_archivo && (
                    <button type="button" aria-label="Ver" title="Ver" className="btn btn-sm btn-outline-primary me-1"
                        disabled={enCurso || cargandoVer} onClick={() => handleVerArchivo(d)}>
                        {cargandoVer ? <Spinner as="span" animation="border" size="sm" /> : <IcoVer />}
                    </button>
                )}
                {d.tiene_archivo && (
                    <button type="button" aria-label="Descargar" title="Descargar" className="btn btn-sm btn-outline-primary me-1"
                        disabled={enCurso} onClick={() => handleDescargar(d)}>
                        {enCurso ? <Spinner as="span" animation="border" size="sm" /> : <IcoDescargar />}
                    </button>
                )}
                {d.link_url && (
                    <button type="button" aria-label="Abrir" title="Abrir enlace externo" className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => handleAbrirLink(d)}><IcoAbrir /></button>
                )}
                {esActivo && puedeEditarDocs && !docFormOpen && (
                    <>
                        <button type="button" aria-label="Reemplazar" title="Reemplazar" className="btn btn-sm btn-outline-warning me-1"
                            disabled={enCurso} onClick={() => abrirSubFormReplace(d)}><IcoReemplazar /></button>
                        <button type="button" aria-label="Quitar" title="Quitar" className="btn btn-sm btn-outline-danger"
                            disabled={enCurso} onClick={() => handleQuitar(d)}><IcoQuitar /></button>
                    </>
                )}
            </>
        );
    };

    return (
        <>
        <Modal show={show} backdrop="static" onHide={() => cerrarModal(false)} keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title as="div" className="w-100 d-flex align-items-center justify-content-between">
                    <span>{yaExiste || invoice?.resultado ? "Resultado" : "Nuevo resultado"} — {invoice?.procedimiento}</span>
                    <span className="badge" style={estiloEstado(badgeEstado)}>{badgeEstado}</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="row small mb-2">
                    <div className="col-md-6"><span className="text-secondary">Paciente</span><div>{invoice?.paciente}</div></div>
                    <div className="col-md-3"><span className="text-secondary">Factura</span><div>{invoice?.numdoc}</div></div>
                    <div className="col-md-3"><span className="text-secondary">Tipo unidad</span><div>{invoice?.result_type}</div></div>
                    <div className="col-12 mt-2"><span className="text-secondary">Procedimiento</span><div>{invoice?.procedimiento}</div></div>
                    {invoice?.result_type === "LAB" && Array.isArray(invoice?.examenes) && (
                        <div className="col-12 mt-1 text-secondary">
                            <small>Exámenes incluidos: {invoice.examenes.length}</small>
                            <ul className="ps-3 mb-0">
                                {invoice.examenes.map((ex) => <li key={ex.id}><small>{ex.nombre}</small></li>)}
                            </ul>
                        </div>
                    )}
                    {esLegacy && (
                        <div className="col-12 mt-1"><small className="text-warning">⚠ Resultado histórico (registrado antes de esta actualización)</small></div>
                    )}
                </div>

                <hr />

                <div className="d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">Documentos y enlaces ({docsActivos.length})</h6>
                    {yaExiste && (
                        <label className="small mb-0">
                            <input type="checkbox" checked={showHistoricos} onChange={(e) => setShowHistoricos(e.target.checked)} />{" "}
                            Mostrar históricos
                        </label>
                    )}
                </div>

                {!yaExiste && (
                    <p className="text-muted small mt-2 mb-0">
                        El resultado se creará automáticamente (estado <strong>Pendiente</strong>) al agregar el primer documento o enlace.
                    </p>
                )}

                {loadingDocs ? (
                    <div className="text-muted small mt-2"><Spinner as="span" animation="border" size="sm" /> Cargando…</div>
                ) : (
                    <>
                        <Table hover size="sm" responsive className="mt-2">
                            <thead>
                                <tr><th>Tipo</th><th>Contenido</th><th>Estado</th><th>Fecha / Autor</th><th className="text-end">Acciones</th></tr>
                            </thead>
                            <tbody>
                                {documentos.length === 0 && (
                                    <tr><td colSpan={5} className="text-muted text-center">Sin documentos.</td></tr>
                                )}
                                {documentos.map((d) => (
                                    <tr key={d.id}
                                        ref={d.id === lastDocId ? filaNuevaRef : null}
                                        className={d.estado !== "Activo" ? "text-muted" : ""}
                                        style={d.id === lastDocId ? { backgroundColor: "#eef7ee", transition: "background-color .4s" } : undefined}>
                                        <td>{d.tipo_documento_nombre || "—"}</td>
                                        <td>
                                            {d.tiene_archivo && <span className="me-2">📄 Archivo</span>}
                                            {d.link_url && <span title={d.link_url}>🔗 {hostDe(d.link_url)}</span>}
                                        </td>
                                        <td>
                                            <Badge bg={d.estado === "Activo" ? "success" : "light"} text={d.estado === "Activo" ? undefined : "dark"}>
                                                {d.estado}
                                            </Badge>
                                        </td>
                                        <td>
                                            <small>
                                                {d.creado_por_nombre ? `Subido por ${d.creado_por_nombre}` : "Autor no registrado"}
                                                {d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ""}
                                            </small>
                                        </td>
                                        <td className="text-end">{accionesFila(d)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {puedeEditarDocs && !docFormOpen && (
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={abrirSubFormCreate}>
                                + Agregar documento o enlace
                            </button>
                        )}

                        {docFormOpen && (
                            <div className="card card-body mt-2">
                                <div className="fw-bold mb-2">
                                    {docMode === "REPLACE" ? `Reemplazar documento (nueva versión)` : "Agregar documento o enlace"}
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small text-secondary mb-1">Tipo de documento</label>
                                    <SelectSearchable listCompany={tipos} handleChange={setDocTipo} selectedOption={docTipo} placeholder="Tipo de documento" isClearable={false} />
                                </div>
                                <div className="mb-1">
                                    <label className="form-label small text-secondary mb-1">Archivo</label>
                                    <input type="file" className="form-control form-control-sm" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />
                                    <div className="form-text">PDF, JPG, JPEG o PNG · máximo 10 MB</div>
                                    {docFileError && <div className="text-danger small">{docFileError}</div>}
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small text-secondary mb-1">Enlace</label>
                                    <input type="text" className="form-control form-control-sm" placeholder="https://..." value={docLink} onChange={(e) => setDocLink(e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small text-secondary mb-1">Descripción</label>
                                    <input type="text" className="form-control form-control-sm" placeholder="(opcional)" value={docDesc} onChange={(e) => setDocDesc(e.target.value)} />
                                </div>
                                <div className="d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-sm btn-light" onClick={resetDocumentForm}>Cancelar</button>
                                    <button type="button" className="btn btn-sm btn-primary" disabled={docSaving} onClick={handleAgregarOReemplazar}>
                                        {docSaving ? <Spinner as="span" animation="border" size="sm" /> : (docMode === "REPLACE" ? "Reemplazar" : "Agregar")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <hr />

                <h6>Información del resultado</h6>
                <div className="row">
                    <div className="col-md-4">
                        <label className="form-label small text-secondary mb-1">Estado</label>
                        <SelectSearchable listCompany={estados} handleChange={(o) => { estadoTocadoRef.current = true; setEstadoSel(o); }} selectedOption={estadoSel} placeholder="Estado" isClearable={false} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small text-secondary mb-1">Usuario responsable</label>
                        <SelectSearchable listCompany={users} handleChange={setUserResp} selectedOption={userResp} placeholder="Usuario responsable" isClearable={true} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small text-secondary mb-1">Usuario informa</label>
                        <SelectSearchable listCompany={users} handleChange={setUserInf} selectedOption={userInf} placeholder="Usuario informa" isClearable={true} />
                    </div>
                </div>
                <div className="row mt-2">
                    <div className="col-md-4">
                        <label className="form-label small text-secondary mb-1" htmlFor="res-fecha-realizacion">
                            Fecha de realización
                        </label>
                        <input
                            id="res-fecha-realizacion"
                            type="date"
                            className="form-control form-control-sm"
                            value={fechaRealizacion}
                            // No se puede haber realizado mañana.
                            max={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setFechaRealizacion(e.target.value)}
                            disabled={!puedeGuardar}
                        />
                        <div className="form-text">Cuándo se realizó el examen. Opcional.</div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={() => cerrarModal(false)}>Cancelar</button>
                <button type="button" className="btn btn-primary" disabled={saving || !estadoSel || !puedeGuardar} onClick={handleGuardar}>
                    {saving ? (<><Spinner as="span" animation="grow" size="sm" /> Por favor espere…</>) : "Guardar"}
                </button>
            </Modal.Footer>
        </Modal>

        <ModalPreviewResultadoDocumento
            data={{
                show: previewOpen,
                handleClose: cerrarPreview,
                documento: previewDoc,
                blobUrl: previewUrl,
                contentType: previewType,
                onDescargar: () => previewDoc && handleDescargar(previewDoc),
            }}
        />
        </>
    );
}

export default ModalResultadoExamen;
